import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Info, Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useSampleData } from "../../hooks/useSampleData";

/* ═══════════════════════════════════════════════════════════════════
   SampleDataBanner — calm dismissible steward-only notice
   --------------------------------------------------------------------
   Renders ONLY when:
     - viewer passes the steward gate
     - the workspace currently carries any sample data
     - the banner hasn't been dismissed in this browser
     - the current route is operational (not auth / landing / client view)

   Surfaces a single calm action ("Clear sample data") that calls the
   canonical clear-sample-data endpoint. Dismissal persists in
   localStorage so refreshing doesn't bring it back.
   ─────────────────────────────────────────────────────────────────── */

const HIDDEN_PATH_PREFIXES = [
  "/", // landing matched separately below
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/accept-invite",
  "/join-deal",
  "/access",
  "/verify-email",
  "/client-view",
  "/solicitor-portal",
  "/pricing",
  "/golden-path",
  "/pipeline-audit",
  "/legacy-landing",
];

const isRouteEligible = (pathname) => {
  if (pathname === "/") return false;
  if (pathname.startsWith("/client-view")) return false;
  return !HIDDEN_PATH_PREFIXES.some(
    (p) => p !== "/" && (pathname === p || pathname.startsWith(`${p}/`)),
  );
};

export const SampleDataBanner = () => {
  const location = useLocation();
  const {
    isSteward,
    hasSampleData,
    counts,
    bannerDismissed,
    dismissBanner,
    clear,
  } = useSampleData();
  const [confirming, setConfirming] = useState(false);
  const [working, setWorking] = useState(false);

  if (!isSteward) return null;
  if (!hasSampleData) return null;
  if (bannerDismissed) return null;
  if (!isRouteEligible(location.pathname)) return null;

  const totalSample = (counts?.contacts || 0) + (counts?.properties || 0) + (counts?.deals || 0);

  const onClear = async () => {
    setWorking(true);
    const res = await clear();
    setWorking(false);
    setConfirming(false);
    if (res?.ok) {
      toast.success(res.summary || "Sample data cleared.");
    } else {
      toast.error(res?.summary || "Could not clear sample data.");
    }
  };

  // iter-188 C4 — compact dismissible chip anchored bottom-right.
  // Was previously a full-width banner consuming page chrome above
  // every Sales Progression / Deal Rooms surface. Now reads as a
  // subtle steward indicator that never crowds the operational view.
  return (
    <div
      className="fixed bottom-[calc(148px+env(safe-area-inset-bottom,0px))] md:bottom-4 right-4 z-40 max-w-[360px]"
      data-testid="sample-data-banner"
    >
      {confirming ? (
        <div
          className="rounded-2xl bg-white border border-slate-100 shadow-[0_8px_28px_-12px_rgba(15,23,42,0.18)] px-3.5 py-3 flex items-center gap-2.5"
          data-testid="sample-data-banner-confirm-shell"
        >
          <span
            className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ background: "rgba(99,102,241,0.06)" }}
            aria-hidden="true"
          >
            <Info size={13} style={{ color: "#6366F1" }} />
          </span>
          <div className="min-w-0">
            <p
              className="text-[12.5px] font-semibold leading-snug text-slate-900"
              data-testid="sample-data-banner-title"
            >
              Clear {totalSample} sample records?
            </p>
            <p
              className="text-[10.5px] leading-snug mt-0.5 text-slate-500"
              data-testid="sample-data-banner-summary"
            >
              Contacts, properties and deals seeded for the demo workspace.
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 ml-1">
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="h-7 px-2.5 rounded-lg text-[11px] font-semibold text-slate-600 bg-white border border-slate-100 hover:bg-slate-50"
              disabled={working}
              data-testid="sample-data-banner-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onClear}
              className="h-7 px-2.5 rounded-lg inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-slate-900 hover:bg-slate-800"
              disabled={working}
              data-testid="sample-data-banner-confirm"
            >
              {working ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <Trash2 size={11} />
              )}
              {working ? "Clearing…" : "Clear"}
            </button>
          </div>
        </div>
      ) : (
        <div
          className="rounded-full bg-white border border-slate-100 shadow-[0_6px_22px_-12px_rgba(15,23,42,0.18)] pl-2.5 pr-1 h-9 inline-flex items-center gap-2"
          data-testid="sample-data-banner-pill"
        >
          <span
            className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
            style={{ background: "rgba(99,102,241,0.08)" }}
            aria-hidden="true"
          >
            <Info size={11} style={{ color: "#6366F1" }} />
          </span>
          <span
            className="text-[11.5px] font-medium text-slate-700 tracking-tight"
            data-testid="sample-data-banner-title"
          >
            Sample workspace
          </span>
          <span
            className="text-[10.5px] text-slate-400 tabular-nums hidden sm:inline"
            data-testid="sample-data-banner-summary"
          >
            · {totalSample} records
          </span>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="ml-1 h-7 px-2.5 rounded-full inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            data-testid="sample-data-banner-clear-cta"
          >
            <Trash2 size={11} />
            Clear
          </button>
          <button
            type="button"
            onClick={dismissBanner}
            className="w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50"
            aria-label="Dismiss"
            data-testid="sample-data-banner-dismiss"
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
};

export default SampleDataBanner;
